/**
 * RTWO Monitoring & Observability Platform — Domain Models
 * Phase 18 | Module ID: RTWO-MON-001
 * Version: 1.0.0-phase18
 *
 * Strongly typed domain models for the RTWO Monitoring & Observability Platform.
 * Enterprise monitoring platform for collecting, visualizing, and managing
 * operational telemetry across the entire RTWO ecosystem.
 *
 * Truthful Data Policy:
 * - No fake CPU, RAM, TPS, latency, alerts, incidents, metrics, logs, or traces.
 * - All panels show truthful empty states awaiting real infrastructure.
 * - Monitoring Platform is NOT claimed as connected to real infrastructure.
 *
 * Source: Monitoring Platform (RTWO-MON-001)
 * Status: Structure Complete — Monitoring Architecture Ready
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type MetricId = string;
export type LogId = string;
export type TraceId = string;
export type AlertId = string;
export type HealthCheckId = string;
export type IncidentId = string;
export type DashboardId = string;
export type ObservabilityPolicyId = string;
export type MONTimestamp = number | null;

export const MON_ENGINE_VERSION = {
  moduleId: 'RTWO-MON-001',
  moduleName: 'RTWO Monitoring & Observability Platform',
  version: '1.0.0-phase18',
  phase: 'Phase 18 — Monitoring & Observability Platform',
  status: 'Structure Complete — Monitoring Architecture Ready',
  productionReadiness: 'Pending Infrastructure Connection',
  policy:
    'Truthful Data Policy Enforced — No Fake CPU, RAM, TPS, Latency, Alerts, Incidents, Metrics, Logs, or Traces',
} as const;

// ─────────────────────────────────────────────
// STATUS TYPES
// ─────────────────────────────────────────────

export type RTWOMonitoringStatus =
  | 'monitoring_architecture_ready'
  | 'waiting_for_real_metrics'
  | 'waiting_for_real_logs'
  | 'waiting_for_real_traces'
  | 'no_alerts'
  | 'no_incidents'
  | 'no_health_checks'
  | 'not_connected'
  | 'not_configured'
  | 'structure_complete'
  | 'production_pending'
  | 'architecture_phase'
  | 'active'
  | 'resolved'
  | 'acknowledged'
  | 'error';

export const MONITORING_STATUS_LABELS: Record<RTWOMonitoringStatus, string> = {
  monitoring_architecture_ready: 'Monitoring Architecture Ready',
  waiting_for_real_metrics: 'Waiting for Real Metrics',
  waiting_for_real_logs: 'Waiting for Real Logs',
  waiting_for_real_traces: 'Waiting for Real Traces',
  no_alerts: 'No Alerts',
  no_incidents: 'No Incidents',
  no_health_checks: 'No Health Checks',
  not_connected: 'Not Connected',
  not_configured: 'Not Configured',
  structure_complete: 'Structure Complete',
  production_pending: 'Production Pending',
  architecture_phase: 'Architecture Phase',
  active: 'Active',
  resolved: 'Resolved',
  acknowledged: 'Acknowledged',
  error: 'Error',
};

// ─────────────────────────────────────────────
// MONITORING CATEGORIES
// ─────────────────────────────────────────────

export type RTWOMonitoringCategory =
  | 'system_health'
  | 'engine_health'
  | 'service_health'
  | 'dependency_health'
  | 'integration_health'
  | 'infrastructure_health'
  | 'security_health'
  | 'application_health';

export const MONITORING_CATEGORY_LABELS: Record<RTWOMonitoringCategory, string> = {
  system_health: 'System Health',
  engine_health: 'Engine Health',
  service_health: 'Service Health',
  dependency_health: 'Dependency Health',
  integration_health: 'Integration Health',
  infrastructure_health: 'Infrastructure Health',
  security_health: 'Security Health',
  application_health: 'Application Health',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOMetric
// ─────────────────────────────────────────────

export type RTWOMetricType =
  'counter' | 'gauge' | 'histogram' | 'summary' | 'rate' | 'percentage' | 'latency' | 'throughput';

export type RTWOMetricStatus =
  'waiting_for_real_metrics' | 'architecture_defined' | 'collecting' | 'stale' | 'error';

export interface RTWOMetric {
  metricId: MetricId;
  metricName: string;
  metricType: RTWOMetricType;
  category: RTWOMonitoringCategory;
  sourceModule: string;
  unit: string;
  valueReference: string;
  labelsReference: string;
  collectionIntervalReference: string;
  retentionPolicyReference: string;
  status: RTWOMetricStatus;
  collectedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOLog
// ─────────────────────────────────────────────

export type RTWOLogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical' | 'audit';

export type RTWOLogStatus =
  'waiting_for_real_logs' | 'architecture_defined' | 'ingesting' | 'indexed' | 'archived' | 'error';

export interface RTWOLog {
  logId: LogId;
  logLevel: RTWOLogLevel;
  category: RTWOMonitoringCategory;
  sourceModule: string;
  messageReference: string;
  contextReference: string;
  traceIdReference: string;
  spanIdReference: string;
  retentionPolicyReference: string;
  status: RTWOLogStatus;
  emittedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOTrace
// ─────────────────────────────────────────────

export type RTWOTraceStatus =
  | 'waiting_for_real_traces'
  | 'architecture_defined'
  | 'sampling'
  | 'complete'
  | 'partial'
  | 'error';

export interface RTWOTrace {
  traceId: TraceId;
  traceName: string;
  category: RTWOMonitoringCategory;
  sourceModule: string;
  spanCountReference: string;
  durationReference: string;
  samplingPolicyReference: string;
  parentTraceReference: string;
  baggage: string;
  status: RTWOTraceStatus;
  startedAt: MONTimestamp;
  completedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOAlert
// ─────────────────────────────────────────────

export type RTWOAlertSeverity = 'info' | 'warning' | 'critical' | 'emergency';

export type RTWOAlertStatus =
  | 'no_alerts'
  | 'architecture_defined'
  | 'firing'
  | 'resolved'
  | 'acknowledged'
  | 'silenced'
  | 'error';

export interface RTWOAlert {
  alertId: AlertId;
  alertName: string;
  severity: RTWOAlertSeverity;
  category: RTWOMonitoringCategory;
  sourceModule: string;
  conditionReference: string;
  thresholdReference: string;
  notificationReference: string;
  incidentReference: string;
  status: RTWOAlertStatus;
  firedAt: MONTimestamp;
  resolvedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOHealthCheck
// ─────────────────────────────────────────────

export type RTWOHealthCheckType =
  'liveness' | 'readiness' | 'startup' | 'dependency' | 'integration' | 'deep';

export type RTWOHealthCheckStatus =
  | 'no_health_checks'
  | 'architecture_defined'
  | 'passing'
  | 'failing'
  | 'degraded'
  | 'unknown'
  | 'error';

export interface RTWOHealthCheck {
  healthCheckId: HealthCheckId;
  checkName: string;
  checkType: RTWOHealthCheckType;
  category: RTWOMonitoringCategory;
  targetModule: string;
  endpointReference: string;
  intervalReference: string;
  timeoutReference: string;
  resultReference: string;
  status: RTWOHealthCheckStatus;
  lastCheckedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOIncident
// ─────────────────────────────────────────────

export type RTWOIncidentSeverity = 'low' | 'medium' | 'high' | 'critical';

export type RTWOIncidentStatus =
  | 'no_incidents'
  | 'architecture_defined'
  | 'open'
  | 'investigating'
  | 'mitigating'
  | 'resolved'
  | 'post_mortem'
  | 'closed';

export interface RTWOIncident {
  incidentId: IncidentId;
  incidentTitle: string;
  severity: RTWOIncidentSeverity;
  category: RTWOMonitoringCategory;
  affectedModules: string[];
  alertReference: string;
  timelineReference: string;
  assigneeReference: string;
  postMortemReference: string;
  status: RTWOIncidentStatus;
  openedAt: MONTimestamp;
  resolvedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWODashboard
// ─────────────────────────────────────────────

export type RTWODashboardType =
  | 'executive_overview'
  | 'metrics'
  | 'logs'
  | 'traces'
  | 'alerts'
  | 'incidents'
  | 'health_checks'
  | 'dependencies'
  | 'observability_policies'
  | 'integration_map'
  | 'architecture_map';

export type RTWODashboardStatus =
  'monitoring_architecture_ready' | 'architecture_defined' | 'active' | 'archived' | 'error';

export interface RTWODashboard {
  dashboardId: DashboardId;
  dashboardName: string;
  dashboardType: RTWODashboardType;
  panelCountReference: string;
  dataSourceReference: string;
  refreshPolicyReference: string;
  accessPolicyReference: string;
  status: RTWODashboardStatus;
  createdAt: MONTimestamp;
  updatedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOObservabilityPolicy
// ─────────────────────────────────────────────

export type RTWOObservabilityPolicyType =
  | 'metrics_retention'
  | 'log_retention'
  | 'trace_sampling'
  | 'alert_routing'
  | 'incident_escalation'
  | 'health_check_schedule'
  | 'notification_routing'
  | 'data_governance';

export type RTWOObservabilityPolicyStatus =
  'architecture_defined' | 'not_configured' | 'active' | 'suspended' | 'deprecated';

export interface RTWOObservabilityPolicy {
  policyId: ObservabilityPolicyId;
  policyName: string;
  policyType: RTWOObservabilityPolicyType;
  category: RTWOMonitoringCategory;
  configurationReference: string;
  enforcementReference: string;
  governanceReference: string;
  status: RTWOObservabilityPolicyStatus;
  createdAt: MONTimestamp;
  updatedAt: MONTimestamp;
}

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED MONITORING CATEGORIES
// ─────────────────────────────────────────────

export const ARCHITECTURE_MONITORING_CATEGORIES: {
  category: RTWOMonitoringCategory;
  label: string;
  description: string;
  targetModules: string[];
}[] = [
  {
    category: 'system_health',
    label: 'System Health',
    description: 'Overall RTWO system health across all engines and services.',
    targetModules: ['Core Engine', 'System Registry', 'Integration Framework'],
  },
  {
    category: 'engine_health',
    label: 'Engine Health',
    description: 'Individual blockchain engine health monitoring.',
    targetModules: [
      'Blockchain Protocol',
      'Consensus',
      'Ledger',
      'P2P',
      'VM',
      'Smart Contract Runtime',
    ],
  },
  {
    category: 'service_health',
    label: 'Service Health',
    description: 'Service-level health for all registered RTWO services.',
    targetModules: ['RPC Platform', 'Indexer', 'Event Bus', 'Developer Platform'],
  },
  {
    category: 'dependency_health',
    label: 'Dependency Health',
    description: 'Health of external and internal dependencies.',
    targetModules: ['Storage Abstraction Layer', 'Cryptography Engine', 'Wallet Engine'],
  },
  {
    category: 'integration_health',
    label: 'Integration Health',
    description: 'Integration Framework connection health and service discovery.',
    targetModules: ['Integration Framework', 'Event Bus', 'RPC Platform'],
  },
  {
    category: 'infrastructure_health',
    label: 'Infrastructure Health',
    description: 'Node, validator, and infrastructure health.',
    targetModules: ['Node Infrastructure', 'Validator Infrastructure'],
  },
  {
    category: 'security_health',
    label: 'Security Health',
    description: 'Security posture, cryptographic health, and access control.',
    targetModules: ['Cryptography & Identity Engine', 'Validator Infrastructure'],
  },
  {
    category: 'application_health',
    label: 'Application Health',
    description: 'Application-layer health for Explorer, Developer Platform, and Asset Engine.',
    targetModules: [
      'Explorer Platform',
      'Developer Platform',
      'Asset & Token Engine',
      'Native Currency Engine',
    ],
  },
];

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED OBSERVABILITY POLICIES
// ─────────────────────────────────────────────

export const ARCHITECTURE_OBSERVABILITY_POLICIES: Omit<
  RTWOObservabilityPolicy,
  'policyId' | 'createdAt' | 'updatedAt'
>[] = [
  {
    policyName: 'Metrics Retention Policy',
    policyType: 'metrics_retention',
    category: 'system_health',
    configurationReference: 'Metrics Retention — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Log Retention Policy',
    policyType: 'log_retention',
    category: 'system_health',
    configurationReference: 'Log Retention — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Trace Sampling Policy',
    policyType: 'trace_sampling',
    category: 'system_health',
    configurationReference: 'Trace Sampling — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Alert Routing Policy',
    policyType: 'alert_routing',
    category: 'system_health',
    configurationReference: 'Alert Routing — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Incident Escalation Policy',
    policyType: 'incident_escalation',
    category: 'system_health',
    configurationReference: 'Incident Escalation — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Health Check Schedule Policy',
    policyType: 'health_check_schedule',
    category: 'system_health',
    configurationReference: 'Health Check Schedule — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Notification Routing Policy',
    policyType: 'notification_routing',
    category: 'system_health',
    configurationReference: 'Notification Routing — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
  {
    policyName: 'Data Governance Policy',
    policyType: 'data_governance',
    category: 'system_health',
    configurationReference: 'Data Governance — Not Configured',
    enforcementReference: 'Enforcement — Not Configured',
    governanceReference: 'Governance — Architecture Phase',
    status: 'not_configured',
  },
];

// ─────────────────────────────────────────────
// ARCHITECTURE-DEFINED DASHBOARDS
// ─────────────────────────────────────────────

export const ARCHITECTURE_DASHBOARDS: Omit<
  RTWODashboard,
  'dashboardId' | 'createdAt' | 'updatedAt'
>[] = [
  {
    dashboardName: 'Executive Overview',
    dashboardType: 'executive_overview',
    panelCountReference: '8 panels defined',
    dataSourceReference: 'Monitoring Platform — Not Connected',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Metrics Dashboard',
    dashboardType: 'metrics',
    panelCountReference: '6 panels defined',
    dataSourceReference: 'Metrics Registry — Waiting for Real Metrics',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Logs Dashboard',
    dashboardType: 'logs',
    panelCountReference: '5 panels defined',
    dataSourceReference: 'Logging Service — Waiting for Real Logs',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Traces Dashboard',
    dashboardType: 'traces',
    panelCountReference: '4 panels defined',
    dataSourceReference: 'Tracing Service — Waiting for Real Traces',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Alerts Dashboard',
    dashboardType: 'alerts',
    panelCountReference: '4 panels defined',
    dataSourceReference: 'Alert Manager — No Alerts',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Incidents Dashboard',
    dashboardType: 'incidents',
    panelCountReference: '4 panels defined',
    dataSourceReference: 'Incident Manager — No Incidents',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Health Checks Dashboard',
    dashboardType: 'health_checks',
    panelCountReference: '5 panels defined',
    dataSourceReference: 'Health Check Service — No Health Checks',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Dependencies Dashboard',
    dashboardType: 'dependencies',
    panelCountReference: '4 panels defined',
    dataSourceReference: 'System Registry — Structure Complete',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Observability Policies',
    dashboardType: 'observability_policies',
    panelCountReference: '3 panels defined',
    dataSourceReference: 'Observability Policy Service — Not Configured',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Integration Map',
    dashboardType: 'integration_map',
    panelCountReference: '2 panels defined',
    dataSourceReference: 'Integration Framework — Structure Complete',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
  {
    dashboardName: 'Architecture Map',
    dashboardType: 'architecture_map',
    panelCountReference: '2 panels defined',
    dataSourceReference: 'System Registry — Structure Complete',
    refreshPolicyReference: 'Refresh — Not Configured',
    accessPolicyReference: 'Access — Architecture Phase',
    status: 'monitoring_architecture_ready',
  },
];
